import { Dropdown } from 'components/dropdown/Dropdown';
import { TextInput } from 'components/forms/TextInput';
import { InlineEditor, noConvert } from 'components/inline_edit/InlineEditor';
import { TextInlineEditorProps } from 'components/inline_edit/TextInlineEditor';
import { useSerializedInlineEditor } from 'custom_hooks/useInlineEditor';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'utils/mergeRefs';
import {
    formatPhoneNumber,
    getCountryInfoFromCode,
    getCountryInfoFromNumber,
    isPhoneNumberValid,
    PhoneCountryInfo,
    phoneCountryInfo,
    toRawPhoneNumber,
} from 'utils/phonenumber';

const defaultCountryInfo = getCountryInfoFromCode('US') as PhoneCountryInfo;

/**
 * Gets the correct *new* caret position (index) after performing formatting on the input
 * phone number.
 */
const getCaretIndex = (
    startCaretIndex: number | null,
    newPhoneNumberInput: string,
    phoneNumberInput: string,
) => {
    // check to see if formatting added any new spaces to the number
    const lastIndexOfPhoneNumber = phoneNumberInput.lastIndexOf(' ');
    const lastIndexOfNewPhoneNumber = newPhoneNumberInput.lastIndexOf(' ');

    // if formatting did not change the amount of spaces in the text input, we
    // do not have to change the start point
    if (lastIndexOfNewPhoneNumber === lastIndexOfPhoneNumber || !startCaretIndex) {
        return startCaretIndex;
    }

    // determine how many number of spaces there are from where the user's cursor point was
    // this allows us to determine how many spaces we need to redirect the cursor point to
    const getInputSpacesCount = (input: string) =>
        input.substr(0, startCaretIndex).match(/ /g)?.length ?? 0;
    const phoneNumberInputSpaces = getInputSpacesCount(phoneNumberInput);
    const newPhoneNumberInputSpaces = getInputSpacesCount(newPhoneNumberInput);

    // spaces were added to the phone number input
    if (lastIndexOfNewPhoneNumber > lastIndexOfPhoneNumber) {
        return startCaretIndex + newPhoneNumberInputSpaces - phoneNumberInputSpaces;
    }

    // formatting removed spaces from the input
    return startCaretIndex - phoneNumberInputSpaces + newPhoneNumberInputSpaces;
};

/**
 * formats phone number for text input value
 */
const formatPhoneNumberInput = (input: string | undefined) => {
    const formattedNumberInput = formatPhoneNumber(input);
    return formattedNumberInput.startsWith('+')
        ? formattedNumberInput.substr(1)
        : formattedNumberInput;
};

/**
 * gets country info for initializing the country state
 */
const getCountryInfo = (input: string | undefined) => {
    const initialCountryInfo = input
        ? getCountryInfoFromNumber(`+${toRawPhoneNumber(input)}`)
        : undefined;
    return initialCountryInfo
        ? {
              countryCode: initialCountryInfo.countryCode,
              callingCode: initialCountryInfo.callingCode,
          }
        : defaultCountryInfo;
};

export type PhoneNumberInlineEditorProps = Readonly<{
    popupRef?: React.RefObject<HTMLDivElement>;
}> &
    Pick<TextInlineEditorProps, 'value' | 'onConfirm' | 'isDisabled' | 'onClose'>;

export function PhoneNumberInlineEditor({
    value,
    onConfirm,
    onClose,
    isDisabled,
    popupRef,
}: PhoneNumberInlineEditorProps) {
    const editorPopupRef = useRef<HTMLDivElement>(null);
    const [country, setCountry] = useState<PhoneCountryInfo>(() => getCountryInfo(value));
    const [countryCodeFilter, setCountryCodeFilter] = useState('');

    const options = useMemo(() => {
        const countryCodes = phoneCountryInfo.map(
            ({ countryCode, callingCode }) =>
                `${countryCode.toUpperCase()} (+${callingCode})`,
        );
        const term = countryCodeFilter.toLowerCase();
        return countryCodes.filter((countryCodeValue) =>
            countryCodeValue.toLowerCase().includes(term),
        );
    }, [countryCodeFilter]);

    const [phoneNumberInput, setPhoneNumberInput] = useState(
        value ? formatPhoneNumber(value).substr(1) : country.callingCode,
    );

    useEffect(() => {
        if (value) {
            setPhoneNumberInput(formatPhoneNumber(value).substring(1));
        }
    }, [value]);

    const validateNumber = (newValue: string | undefined) => {
        if (!newValue) {
            return undefined;
        }
        if (newValue && !isPhoneNumberValid(newValue, country.countryCode)) {
            return 'Value must be a valid number';
        }
        return undefined;
    };

    const {
        error,
        setInputValue,
        isEditing,
        isConfirming,
        openEditor,
        closeEditor,
        confirm,
        canConfirm,
    } = useSerializedInlineEditor<string, string>({
        value,
        validate: validateNumber,
        onConfirm,
        onClose,
        deserialize: noConvert,
        serialize: noConvert,
    });

    // triggered when dropdown value is changed, input value is cleared and country is updated
    const onSelectChange = (selectedValue: string) => {
        const selectedCountryCode = selectedValue.split(' ')[0].toUpperCase();
        const info = getCountryInfoFromCode(selectedCountryCode);
        if (info) {
            setPhoneNumberInput(info.callingCode);
            setCountry(info);
        }
    };

    // triggered when text input value is changed, formats phone number and checks to see if country info has changed
    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();

        // clean the input for any non-digit values.
        const phoneNumber = event.target.value
            ? `+${toRawPhoneNumber(event.target.value)}`
            : undefined;

        // format phone number and set value as well as the value displayed in the text input
        const newPhoneNumberInput = formatPhoneNumberInput(phoneNumber);
        setInputValue(phoneNumber || '');
        setPhoneNumberInput(newPhoneNumberInput);

        // find proper caret index after phone number reformats
        const caretIndex = getCaretIndex(
            event.target.selectionStart,
            newPhoneNumberInput,
            phoneNumberInput,
        );

        event.target.value = newPhoneNumberInput;
        // set cursor to appropriate caret index
        event.target.setSelectionRange(caretIndex, caretIndex);

        // guess the country code
        const phoneNumberCountry = phoneNumber
            ? getCountryInfoFromNumber(phoneNumber)
            : undefined;

        if (phoneNumberCountry && phoneNumberCountry !== country) {
            setCountry(phoneNumberCountry);
        }
    };

    return (
        <InlineEditor
            value={formatPhoneNumber(value) || 'No phone number'}
            editor={
                <div className="flex-row align-center">
                    <Dropdown<string>
                        value={country.countryCode.toUpperCase()}
                        filterable={{
                            filterText: countryCodeFilter,
                            onChange: setCountryCodeFilter,
                            placeholder: 'Search',
                        }}
                        renderLabel={({ value: selectedValue }) => selectedValue}
                        renderItem={({ item }) => item}
                        dataKey={(item) => item}
                        onChange={onSelectChange}
                        data={options}
                        popup={{
                            width: '15rem',
                            popupRef: mergeRefs(popupRef, editorPopupRef),
                        }}
                    />
                    <div>+</div>
                    <TextInput
                        className="inline-block full-width"
                        placeholder="Enter a valid number"
                        value={phoneNumberInput}
                        invalid={error !== undefined}
                        onChange={onInputChange}
                        disabled={isDisabled}
                    />
                </div>
            }
            error={error}
            canClose={!isConfirming}
            canEdit={!isConfirming}
            canConfirm={canConfirm}
            isEditing={isEditing}
            isDisabled={isDisabled || isConfirming}
            isPending={isConfirming}
            onClose={closeEditor}
            onEdit={openEditor}
            onConfirm={confirm}
            contentRefs={[editorPopupRef]}
            shouldTextWrap={true}
        />
    );
}
